/**
 * Session service types and interfaces
 */

import { serverTimestamp, Timestamp, type DocumentSnapshot, type QueryDocumentSnapshot } from 'firebase/firestore'
import type { Session } from '../../types'

/** Location data for clock operations */
export interface LocationData {
  lat: number
  lng: number
  accuracy: number
}

/** Clock in parameters */
export interface ClockInParams {
  userId: string
  userName: string
  location: LocationData
  photos: [Blob, Blob]
  siteId?: string
  siteName?: string
}

/** Clock out parameters */
export interface ClockOutParams {
  sessionId: string
  location: LocationData
  photos: [Blob, Blob]
  dailyReport: string
}

/** Break operation parameters */
export interface BreakParams {
  sessionId: string
  location?: LocationData | null
}

/** End break parameters */
export interface EndBreakParams {
  sessionId: string
  breakId: string
  location?: LocationData | null
}

/** Session query options */
export interface SessionQueryOptions {
  userId?: string
  startDate?: string
  endDate?: string
  status?: Session['status']
  limit?: number
}

/** Paginated query options */
export interface PaginatedSessionQueryOptions extends Omit<SessionQueryOptions, 'limit'> {
  pageSize?: number
  cursor?: DocumentSnapshot | null
}

/** Paginated result type */
export interface PaginatedResult<T> {
  data: T[]
  lastDoc: QueryDocumentSnapshot | null
  hasMore: boolean
}

/** Manual entry parameters */
export interface ManualEntryParams {
  userId: string
  userName: string
  date: string
  checkInTime: string
  checkOutTime: string
  reason: string
  adminId: string
}

/** Check point data stored in session */
export interface CheckPointData {
  timestamp: ReturnType<typeof serverTimestamp> | Timestamp
  time: string
  lat: number | null
  lng: number | null
  accuracy: number | null
  locationHash?: string // Geohash for privacy-preserving location verification
  address: string
  photo1Url: string | null
  photo2Url: string | null
  isMockLocation: boolean
  deviceInfo: string
}

/** Break data stored in session */
export interface BreakData {
  id: string
  startTime: Timestamp
  startLat: number | null
  startLng: number | null
  endTime: Timestamp | null
  endLat: number | null
  endLng: number | null
  duration: number | null
}

/** Session document from Firestore */
export interface SessionDocument {
  userId: string
  userName: string
  date: string
  siteId: string | null
  siteName: string | null
  checkIn: CheckPointData
  checkOut: CheckPointData | null
  breaks: BreakData[]
  dailyReport: string | null
  totalWorkMinutes: number | null
  totalBreakMinutes: number | null
  grossPay: number | null
  status: Session['status']
  flagReason: string | null
  approved: boolean
  approvedAt: ReturnType<typeof serverTimestamp> | null
  approvedBy: string | null
  isManualEntry: boolean
  manualEntryReason: string | null
  manualEntryBy: string | null
  manualEntryAt: ReturnType<typeof serverTimestamp> | null
  createdAt: ReturnType<typeof serverTimestamp>
  updatedAt: ReturnType<typeof serverTimestamp>
}

/** Session with ID */
export type SessionWithId = { id: string } & SessionDocument

/** Sessions collection name */
export const SESSIONS_COLLECTION = 'sessions'
